/**
 * D-EZ-110 — THE LEAGUE'S STORY: eras and dynasties named from the record, and
 * the league season by season — its champion, the era it belongs to, and the
 * storyline that defined it. Career → League history.
 *
 * The era names follow written rules (`domain/chapters/eras.ts`), so the same
 * history always gets the same names; the page prints each era's reason under
 * its name.
 */

import { useMemo } from 'react';
import { leagueTimeline, type ChapterSeason } from '../../domain/chapters/index.ts';

export default function LeagueTimeline({
  seasons,
  onDriver,
}: {
  seasons: readonly ChapterSeason[];
  onDriver?: (driverId: string) => void;
}) {
  const t = useMemo(() => {
    try {
      return leagueTimeline(seasons);
    } catch {
      return null;
    }
  }, [seasons]);
  if (!t || t.seasons.length === 0) return <p className="chap__note">The league’s story starts with its first race.</p>;
  const eraTitle = new Map(t.eras.map((e) => [e.key, e]));
  return (
    <section className="tl readable" aria-label="The league’s story">
      <header className="chap__head">
        <p className="chap__kicker">The league’s story</p>
        <h2 className="chap__headline">{t.eras.length > 0 ? t.eras.map((e) => e.title.text).join(' · ') : 'Every season so far'}</h2>
        <p className="chap__dek">
          Eras are named by rule from the champions’ list: a driver’s two titles within three seasons make his years; a
          shop’s three, or two by different men, its dynasty; three or more seasons in nobody’s era are the open years.
        </p>
      </header>
      {t.eras.length > 0 && (
        <ul className="tl__eras" aria-label="Eras">
          {t.eras.map((e) => (
            <li key={e.key} className={`tl__era tl__era--${e.kind}`}>
              <span className="tl__eraname">{e.title.text}</span>
              <span className="tl__eraspan">
                {e.from === e.to ? `Season ${e.from}` : `Seasons ${e.from}–${e.to}`}
                {e.ongoing ? ' · still going' : ''}
              </span>
              <span className="tl__eraline">{e.line.text}</span>
            </li>
          ))}
        </ul>
      )}
      <ol className="tl__seasons">
        {[...t.seasons].reverse().map((s) => (
          <li key={s.seasonNumber} className="tl__season">
            <div className="tl__n" aria-label={`Season ${s.seasonNumber}`}>{s.seasonNumber}</div>
            <div className="tl__body">
              <p className="tl__champ">
                {s.champion ? (
                  <>
                    {onDriver ? (
                      <button type="button" className="linkbtn" onClick={() => onDriver(s.champion!.driverId)}>
                        {s.champion.name}
                      </button>
                    ) : (
                      s.champion.name
                    )}
                    {s.champion.team ? <span className="tl__team"> · {s.champion.team}</span> : null}
                  </>
                ) : s.complete ? (
                  'No champion'
                ) : (
                  <span className="tl__live">In progress</span>
                )}
              </p>
              {s.eras.length > 0 && (
                <p className="tl__bands">
                  {s.eras.map((k) => {
                    const e = eraTitle.get(k);
                    return e ? <span key={k} className={`tl__band tl__band--${e.kind}`}>{e.title.text}</span> : null;
                  })}
                </p>
              )}
              {s.storyline && <p className="tl__story">{s.storyline.text}</p>}
              {s.also.length > 0 && (
                <ul className="tl__also">
                  {s.also.map((a) => <li key={a.text}>{a.text}</li>)}
                </ul>
              )}
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}
